import React, { useEffect, useState, useContext } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import toastError from "../../errors/toastError";
import {
  listBoards,
  showBoard,
  moveTicketToLane
} from "../../services/kanbanEnterpriseService";

const useStyles = makeStyles((theme) => ({
  fieldLabel: {
    display: "block",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: 2,
    marginTop: 4,
    fontSize: "0.7rem"
  },
  select: {
    "& .MuiSelect-root": {
      display: "flex",
      alignItems: "center",
      minHeight: 18,
      paddingTop: 8,
      paddingBottom: 8,
      fontSize: "0.8rem"
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 8
    }
  },
  sectionTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    marginBottom: 2,
    marginTop: 2
  },
  placeholder: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    fontSize: "0.8rem"
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 8,
    flexShrink: 0
  },
  menuColorDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 10,
    flexShrink: 0
  },
  emptyMsg: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    fontSize: "0.75rem"
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "2px 0"
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
    width: "100%"
  },
  halfField: {
    flex: 1,
    minWidth: 0
  }
}));

/**
 * Deriva o `updateData` do move a partir do tipo/filtro da coluna destino,
 * espelhando a lógica do board Enterprise (status/fila/atendente).
 * Obs: o backend espera `assigneeId` (não `userId`) para trocar o dono.
 */
const sameId = (a, b) =>
  a != null && b != null && a !== "" && b !== "" && Number(a) === Number(b);

const deriveUpdateData = (column) => {
  const filter = column?.filterJson || {};
  const updateData = {};

  if (
    column?.type === "status" &&
    Array.isArray(filter.status) &&
    filter.status.length === 1
  ) {
    updateData.status = filter.status[0];
  }

  if (
    column?.type === "queue" &&
    Array.isArray(filter.queueIds) &&
    filter.queueIds.length === 1
  ) {
    updateData.queueId = filter.queueIds[0];
  }

  if (
    column?.type === "assignee" &&
    Array.isArray(filter.userIds) &&
    filter.userIds.length === 1
  ) {
    updateData.assigneeId = filter.userIds[0];
  }

  return updateData;
};

export function KanbanEnterpriseTicketStage({ ticket }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [enabled, setEnabled] = useState(false);
  const [checkingPlan, setCheckingPlan] = useState(true);

  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [columns, setColumns] = useState([]);
  const [selectedColumnId, setSelectedColumnId] = useState("");

  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [moving, setMoving] = useState(false);

  // 1) Verificar se o plano da empresa libera o Kanban Enterprise
  useEffect(() => {
    let mounted = true;

    const check = async () => {
      const flagFromTicket = ticket?.company?.plan?.useKanbanEnterprise;
      if (typeof flagFromTicket === "boolean") {
        if (mounted) {
          setEnabled(flagFromTicket);
          setCheckingPlan(false);
        }
        return;
      }

      try {
        const companyId = user?.companyId;
        if (!companyId) {
          if (mounted) setEnabled(false);
          return;
        }
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (mounted) setEnabled(!!planConfigs?.plan?.useKanbanEnterprise);
      } catch (err) {
        if (mounted) setEnabled(false);
      } finally {
        if (mounted) setCheckingPlan(false);
      }
    };

    check();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  // 2) Carregar os quadros (boards) quando habilitado
  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;

    const load = async () => {
      setLoadingBoards(true);
      try {
        const data = await listBoards();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : [];
        setBoards(list);

        // Board inicial: o que contém a coluna atual do ticket; senão o primeiro
        // (a API já ordena isDefault primeiro).
        const currentColId =
          ticket?.kanbanColumnId ?? ticket?.kanbanColumn?.id;
        let initialBoard = null;
        if (currentColId) {
          initialBoard = list.find((b) =>
            (b.columns || []).some((c) => sameId(c.id, currentColId))
          );
          if (!initialBoard && ticket?.kanbanColumn?.boardId) {
            initialBoard = list.find((b) =>
              sameId(b.id, ticket.kanbanColumn.boardId)
            );
          }
        }
        if (!initialBoard) initialBoard = list[0];
        setSelectedBoardId(initialBoard ? initialBoard.id : "");
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoadingBoards(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ticket?.id, ticket?.kanbanColumnId]);
  useEffect(() => {
    if (!enabled || !selectedBoardId) {
      setColumns([]);
      setSelectedColumnId("");
      return undefined;
    }
    let mounted = true;

    const load = async () => {
      setLoadingColumns(true);
      try {
        const board = await showBoard(selectedBoardId);
        if (!mounted) return;
        const cols = (board?.columns || [])
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        setColumns(cols);

        const currentColId =
          ticket?.kanbanColumnId ?? ticket?.kanbanColumn?.id;
        const matched = cols.find((c) => sameId(c.id, currentColId));
        setSelectedColumnId(matched ? matched.id : "");
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoadingColumns(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedBoardId, ticket?.id, ticket?.kanbanColumnId]);

  // 4) Sincronizar Quadro/Etapa quando ticket.kanbanColumnId chega/atualiza
  useEffect(() => {
    if (!enabled || moving || loadingBoards || loadingColumns) return;

    const colId = ticket?.kanbanColumnId ?? ticket?.kanbanColumn?.id ?? "";

    if (!colId) {
      if (selectedColumnId) setSelectedColumnId("");
      return;
    }

    const boardWithCol = boards.find((b) =>
      (b.columns || []).some((c) => sameId(c.id, colId))
    ) || (ticket?.kanbanColumn?.boardId
      ? boards.find((b) => sameId(b.id, ticket.kanbanColumn.boardId))
      : null);

    if (boardWithCol && !sameId(boardWithCol.id, selectedBoardId)) {
      setSelectedBoardId(boardWithCol.id);
      return;
    }

    const matched = columns.find((c) => sameId(c.id, colId));
    if (matched && !sameId(selectedColumnId, matched.id)) {
      setSelectedColumnId(matched.id);
    }
  }, [
    enabled,
    moving,
    loadingBoards,
    loadingColumns,
    ticket?.kanbanColumnId,
    ticket?.kanbanColumn?.id,
    ticket?.kanbanColumn?.boardId,
    boards,
    columns,
    selectedBoardId,
    selectedColumnId,
  ]);

  const handleBoardChange = (e) => {
    setSelectedBoardId(e.target.value);
  };

  const handleColumnChange = async (e) => {
    const columnId = e.target.value;
    const previous = selectedColumnId;

    setSelectedColumnId(columnId);
    if (!columnId) return;

    const column = columns.find((c) => sameId(c.id, columnId));
    if (!column) return;

    setMoving(true);
    try {
      const updateData = deriveUpdateData(column);
      await moveTicketToLane(ticket.id, {
        columnId: Number(column.id),
        updateData,
      });
      toast.success(`Ticket movido para "${column.name}"`);
    } catch (err) {
      setSelectedColumnId(previous);
      toastError(err);
    } finally {
      setMoving(false);
    }
  };

  if (checkingPlan || !enabled) return null;

  const selectedColumn = columns.find((c) =>
    sameId(c.id, selectedColumnId)
  );

  const renderColumnValue = () => {
    if (!selectedColumn) {
      return <span className={classes.placeholder}>Selecione uma etapa</span>;
    }
    return (
      <Box display="flex" alignItems="center">
        <span
          className={classes.colorDot}
          style={{ backgroundColor: selectedColumn.color || "#757575" }}
        />
        <span>{selectedColumn.name}</span>
      </Box>
    );
  };

  return (
    <>
      <Typography className={classes.sectionTitle}>
        Kanban Enterprise
      </Typography>

      {loadingBoards ? (
        <div className={classes.loadingRow}>
          <CircularProgress size={16} />
          <Typography variant="caption">Carregando quadros...</Typography>
        </div>
      ) : boards.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          Nenhum quadro do Kanban Enterprise configurado.
        </Typography>
      ) : (
        <>
          <div className={classes.row}>
            <div className={classes.halfField}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Quadro
              </Typography>
              <FormControl fullWidth margin="dense" variant="outlined">
                <Select
                  className={classes.select}
                  value={selectedBoardId || ""}
                  onChange={handleBoardChange}
                  displayEmpty
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" }
                  }}
                >
                  {boards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className={classes.halfField}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Etapa
              </Typography>
              <FormControl fullWidth margin="dense" variant="outlined">
                <Select
                  className={classes.select}
                  value={selectedColumnId || ""}
                  onChange={handleColumnChange}
                  displayEmpty
                  disabled={loadingColumns || moving || columns.length === 0}
                  renderValue={renderColumnValue}
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: { style: { maxHeight: 320 } }
                  }}
                >
                  <MenuItem value="">
                    <em>Selecione uma etapa</em>
                  </MenuItem>
                  {columns.map((column) => (
                    <MenuItem key={column.id} value={column.id}>
                      <span
                        className={classes.menuColorDot}
                        style={{ backgroundColor: column.color || "#757575" }}
                      />
                      {column.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          {(loadingColumns || moving) && (
            <div className={classes.loadingRow}>
              <CircularProgress size={14} />
              <Typography variant="caption">
                {moving ? "Movendo ticket..." : "Carregando etapas..."}
              </Typography>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default KanbanEnterpriseTicketStage;
